import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Chip,
  IconButton, Tooltip, Alert, CircularProgress, Autocomplete, Grid,
  InputAdornment, Checkbox, RadioGroup, Radio, FormControlLabel, FormLabel,
  Collapse, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const API = '/api/contabilidad';

const fmtFecha = s => {
  if (!s) return '';
  const [y, m, d] = String(s).substring(0, 10).split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : String(s).substring(0, 10);
};

const TIPOS_DOC_COMPRA = [
  { code: '01', label: '01 — Factura' },
  { code: '02', label: '02 — Recibo por Honorarios (RHE)' },
  { code: '04', label: '04 — Liquidación de Compra' },
  { code: '07', label: '07 — Nota de Crédito' },
  { code: '08', label: '08 — Nota de Débito' },
  { code: '14', label: '14 — Recibo Servicios Públicos' },
  { code: '18', label: '18 — Documento Bancario' },
  { code: '23', label: '23 — Póliza de Adjudicación' },
  { code: '50', label: '50 — Declaración de Aduanas (DUA)' },
  { code: '91', label: '91 — Comprobante no domiciliado' },
];

const TASA_RETENCION_RHE = 0.08;
const IGV_RATE = 0.18;

const EMPTY_FORM = {
  tipo_documento: '01', serie: '', numero: '',
  fecha_emision: '', fecha_vencimiento: '',
  moneda: 'PEN', tipo_cambio: '1.0000',
  tipo_doc_identidad: '6', num_documento: '', razon_social: '',
  base_gravada: '', igv: '', igv_credito_fiscal: '',
  base_exonerada: '', base_inafecta: '',
  isc: '', otros_tributos: '', total: '',
  renta_4ta: '', glosa: '', destino_operacion: 'GRAVADA_EXCLUSIVA', centro_costo_id: '',
  // Datos SUNAT adicionales (opcionales) — doc. de referencia para NC/ND, detracción,
  // retención, percepción, y código de aduana para DUA/DAM.
  doc_ref_tipo: '', doc_ref_serie: '', doc_ref_numero: '',
  tiene_detraccion: false, det_fecha: '', det_numero: '', det_tasa: '', det_monto: '', det_tipo: '',
  tiene_retencion: false, ret_codigo_tasa: '', ret_numero: '',
  tiene_percepcion: false, per_codigo_tasa: '', per_numero: '',
  aduana_codigo: '',
};

const DESTINO_OPERACION_COMPRA = [
  { value: 'GRAVADA_EXCLUSIVA', label: 'Destinada solo a operaciones gravadas y/o de exportación' },
  { value: 'GRAVADA_Y_NO_GRAVADA', label: 'Destinada a operaciones gravadas y no gravadas (prorrateo)' },
  { value: 'NO_GRAVADA', label: 'Destinada solo a operaciones no gravadas' },
];

// Mismo catálogo que ya usa Tesorería (frontend/src/pages/Tesoreria.js) — se reutiliza
// tal cual para que ambos módulos hablen el mismo idioma.
const MEDIOS_PAGO = ['EFECTIVO', 'TRANSFERENCIA', 'TARJETA', 'CHEQUE', 'YAPE', 'PLIN', 'OTRO'];

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function chipEstado(estado) {
  const map = { PENDIENTE: 'warning', CANCELADO: 'success', ANULADO: 'error' };
  return <Chip label={estado} color={map[estado] || 'default'} size="small" />;
}

export default function RegistroCompras() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [periodoSel, setPeriodoSel] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [igvManual, setIgvManual] = useState(false);
  const [showAdicionales, setShowAdicionales] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [buscar, setBuscar] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [pagoDialog, setPagoDialog] = useState(false);
  const [pagoDoc, setPagoDoc] = useState(null);
  const [pagoForm, setPagoForm] = useState({ fecha_cancelacion: '', cuenta_cobpag_id: '', monto_cancelado: '', glosa: '', medio_pago: 'EFECTIVO', pago_referencia: '' });
  const [cuentasCaja, setCuentasCaja] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [snack, setSnack] = useState('');
  const [consultandoDoc, setConsultandoDoc] = useState(false);
  const [consultaErr, setConsultaErr] = useState('');

  // Importación por plantilla Excel
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState('');

  // Selección múltiple + cancelación masiva
  const [seleccionados, setSeleccionados] = useState([]);
  const [cancelMasivoOpen, setCancelMasivoOpen] = useState(false);
  const [cancelMasivoForm, setCancelMasivoForm] = useState({
    modo: 'individual', fecha_cancelacion: '', cuenta_cobpag_id: '',
    medio_pago: 'EFECTIVO', pago_referencia: '', usar_fecha_emision: false,
  });
  const [cancelMasivoErr, setCancelMasivoErr] = useState('');
  const [cancelandoMasivo, setCancelandoMasivo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a, b) => b.periodo.localeCompare(a.periodo));
      setPeriodos(r.data);
      if (abiertos.length > 0 && !periodoSel) setPeriodoSel(abiertos[0].periodo);
    }).catch(() => {});
    axios.get(`${API}/plan?empresa_id=${empresaId}&solo_hojas=1&buscar=10`).then(r => setCuentasCaja(r.data)).catch(() => {});
    axios.get(`${API}/centros-costo`, { params: { empresa_id: empresaId } })
      .then(r => setCentrosCosto(Array.isArray(r.data) ? r.data.filter(c => c.activo) : []))
      .catch(() => {});
  }, [empresaId]);

  const empresaSel = empresas.find(e => String(e.id) === String(empresaId));
  const usaCentroCosto = !!empresaSel?.usa_centro_costo;

  const fetchDocs = useCallback(async () => {
    if (!empresaId || !periodoSel) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/doc-registro`, {
        params: { empresa_id: empresaId, periodo: periodoSel, tipo_operacion: 'COMPRA', estado: filtroEstado || undefined, buscar: buscar || undefined }
      });
      setDocs(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, periodoSel, filtroEstado, buscar]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const isRhe = form.tipo_documento === '02';

  function calcularAutomatico(f) {
    const base = parseFloat(f.base_gravada) || 0;
    const exo = parseFloat(f.base_exonerada) || 0;
    const ina = parseFloat(f.base_inafecta) || 0;
    const isc = parseFloat(f.isc) || 0;
    const otros = parseFloat(f.otros_tributos) || 0;

    if (f.tipo_documento === '02') {
      const ret = +(base * TASA_RETENCION_RHE).toFixed(2);
      const neto = +(base - ret).toFixed(2);
      return { ...f, renta_4ta: ret.toFixed(2), igv: '0.00', igv_credito_fiscal: '0.00', total: neto.toFixed(2) };
    }

    const igvCalc = +(base * IGV_RATE).toFixed(2);
    const manual = arguments.length > 1 ? arguments[1] : igvManual;
    const igv = manual ? (parseFloat(f.igv) || 0) : igvCalc;
    const total = +(base + igv + exo + ina + isc + otros).toFixed(2);
    return { ...f, igv: igv.toFixed(2), igv_credito_fiscal: igv.toFixed(2), total: total.toFixed(2) };
  }

  function handleChange(e) {
    const { name, value } = e.target;
    let upd = { ...form, [name]: value };
    if (['base_gravada', 'igv', 'base_exonerada', 'base_inafecta', 'isc', 'otros_tributos', 'tipo_documento'].includes(name)) {
      upd = calcularAutomatico(upd);
    }
    setForm(upd);
    if (name === 'fecha_emision' && upd.moneda === 'USD') autocompletarTcCompra(value);
    if (name === 'moneda' && value === 'USD' && upd.fecha_emision) autocompletarTcCompra(upd.fecha_emision);
  }

  // Compras usa TC VENTA, igual que ventas (Art. 17 del Reglamento del IGV: "la conversión en
  // moneda nacional se efectuará al tipo de cambio promedio ponderado VENTA" para toda
  // operación en moneda extranjera, salvo importaciones). Busca primero en la tabla ya
  // sincronizada; si no hay dato para esa fecha, consulta la API de SUNAT en vivo (mismo
  // patrón de 2 pasos que ya usa Factura.js en el ERP principal). El campo queda editable
  // igual, por si el contador necesita corregirlo.
  async function autocompletarTcCompra(fecha) {
    if (!fecha) return;
    try {
      const resBD = await axios.get('/api/tipo-cambio');
      const tc = resBD.data.find(x => x.fecha && x.fecha.substring(0, 10) === fecha);
      if (tc && tc.venta) { setForm(f => ({ ...f, tipo_cambio: tc.venta })); return; }
    } catch {}
    try {
      const res = await axios.get(`/api/tipo-cambio/sunat?fecha=${fecha}`);
      if (res.data && res.data.venta) { setForm(f => ({ ...f, tipo_cambio: res.data.venta })); }
    } catch {}
  }

  // NC/ND: hereda moneda y TC del documento que referencia (igual que NotaCredito.js en el
  // ERP principal) — para que la nota cancele exactamente en la misma moneda/valor, sin una
  // diferencia de cambio artificial solo por usar la fecha de la propia nota.
  async function autocompletarDesdeReferencia(serie, numero) {
    if (!serie || !numero || !empresaId) return;
    try {
      const r = await axios.get(`${API}/doc-registro/referencia/buscar`, {
        params: { empresa_id: empresaId, tipo_operacion: 'COMPRA', serie, numero }
      });
      if (r.data) {
        setForm(f => ({ ...f, moneda: r.data.moneda || f.moneda, tipo_cambio: r.data.tipo_cambio || f.tipo_cambio }));
      }
    } catch {}
  }

  function handleToggleIgvManual(checked) {
    setIgvManual(checked);
    setForm(f => calcularAutomatico(f, checked));
  }

  async function consultarDocumento() {
    const doc = form.num_documento.trim();
    if (!doc) return;
    setConsultandoDoc(true);
    setConsultaErr('');
    try {
      if (form.tipo_doc_identidad === '6') {
        const r = await axios.get(`/api/consulta-peru/ruc/${doc}`);
        if (r.data?.razonSocial || r.data?.nombre) {
          setForm(f => ({ ...f, razon_social: r.data.razonSocial || r.data.nombre || '' }));
        } else {
          setConsultaErr('RUC no encontrado en SUNAT');
        }
      } else if (form.tipo_doc_identidad === '1') {
        const r = await axios.get(`/api/consulta-peru/dni/${doc}`);
        if (r.data?.nombre || r.data?.nombres) {
          const nombre = r.data.nombre || `${r.data.nombres || ''} ${r.data.apellidoPaterno || ''} ${r.data.apellidoMaterno || ''}`.trim();
          setForm(f => ({ ...f, razon_social: nombre }));
        } else {
          setConsultaErr('DNI no encontrado en RENIEC');
        }
      }
    } catch {
      setConsultaErr('Error al consultar. Verifique el número.');
    } finally {
      setConsultandoDoc(false);
    }
  }

  async function handleSave() {
    setSaveErr('');
    try {
      await axios.post(`${API}/doc-registro`, {
        ...form,
        empresa_id: empresaId,
        periodo: periodoSel,
        tipo_operacion: 'COMPRA',
        base_gravada: parseFloat(form.base_gravada) || 0,
        igv: parseFloat(form.igv) || 0,
        igv_credito_fiscal: parseFloat(form.igv_credito_fiscal) || 0,
        base_exonerada: parseFloat(form.base_exonerada) || 0,
        base_inafecta: parseFloat(form.base_inafecta) || 0,
        isc: parseFloat(form.isc) || 0,
        otros_tributos: parseFloat(form.otros_tributos) || 0,
        total: parseFloat(form.total) || 0,
        renta_4ta: parseFloat(form.renta_4ta) || 0,
        tipo_cambio: parseFloat(form.tipo_cambio) || 1,
      });
      setOpen(false);
      setSnack('Documento registrado y asiento generado');
      fetchDocs();
    } catch (e) {
      setSaveErr(e.response?.data?.error || 'Error al guardar');
    }
  }

  async function handleAnular(id) {
    if (!window.confirm('¿Anular este documento? Se anularán sus asientos contables.')) return;
    try {
      await axios.put(`${API}/doc-registro/${id}/anular`);
      setSnack('Documento anulado');
      fetchDocs();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  }

  async function handlePago() {
    if (!pagoForm.fecha_cancelacion || !pagoForm.cuenta_cobpag_id) {
      alert('Complete fecha y cuenta de pago');
      return;
    }
    try {
      await axios.put(`${API}/doc-registro/${pagoDoc.id}/cancelar`, {
        ...pagoForm,
        monto_cancelado: parseFloat(pagoForm.monto_cancelado) || parseFloat(pagoDoc.total),
      });
      setPagoDialog(false);
      setSnack('Pago registrado y asiento generado');
      fetchDocs();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  }

  async function handleDescargarPlantilla() {
    try {
      const r = await axios.get(`${API}/doc-registro/plantilla`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'Plantilla_Ventas_Compras.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Error al descargar la plantilla'); }
  }

  async function handleImportarPreview(file) {
    setImporting(true); setImportErr(''); setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('empresa_id', empresaId);
      fd.append('generar_asientos', '1');
      fd.append('solo_preview', '1');
      const r = await axios.post(`${API}/doc-registro/importar`, fd);
      setImportPreview(r.data);
    } catch (e) { setImportErr(e.response?.data?.error || 'Error al leer el archivo'); }
    setImporting(false);
  }

  async function handleImportarConfirmar() {
    if (!importFile) return;
    setImporting(true); setImportErr('');
    try {
      const fd = new FormData();
      fd.append('archivo', importFile);
      fd.append('empresa_id', empresaId);
      fd.append('generar_asientos', '1');
      fd.append('solo_preview', '0');
      const r = await axios.post(`${API}/doc-registro/importar`, fd);
      setSnack(`${r.data.importados} documento(s) importado(s)` + (r.data.errores?.length ? ` — ${r.data.errores.length} advertencia(s)` : ''));
      setImportOpen(false); setImportFile(null); setImportPreview(null);
      fetchDocs();
    } catch (e) { setImportErr(e.response?.data?.error || 'Error al importar'); }
    setImporting(false);
  }

  function toggleSeleccion(id) {
    setSeleccionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  const pendientesDocs = docs.filter(d => d.estado === 'PENDIENTE');
  const seleccionadosDocs = docs.filter(d => seleccionados.includes(d.id));
  const totalSeleccionado = seleccionadosDocs.reduce((s, d) => s + parseFloat(d.total || 0), 0);

  async function handleCancelarMasivo() {
    if (!cancelMasivoForm.usar_fecha_emision && !cancelMasivoForm.fecha_cancelacion) {
      setCancelMasivoErr('Complete la fecha de pago (o marque "usar fecha de emisión")'); return;
    }
    if (!cancelMasivoForm.cuenta_cobpag_id) {
      setCancelMasivoErr('Seleccione la cuenta de pago'); return;
    }
    setCancelandoMasivo(true); setCancelMasivoErr('');
    try {
      const r = await axios.put(`${API}/doc-registro/cancelar-masivo`, {
        ids: seleccionados, modo: cancelMasivoForm.modo,
        fecha_cancelacion: cancelMasivoForm.fecha_cancelacion,
        cuenta_cobpag_id: cancelMasivoForm.cuenta_cobpag_id,
        medio_pago: cancelMasivoForm.medio_pago,
        pago_referencia: cancelMasivoForm.pago_referencia,
        usar_fecha_emision: cancelMasivoForm.usar_fecha_emision,
      });
      setSnack(`${r.data.cancelados} documento(s) cancelado(s) en ${r.data.modo === 'agrupado' ? '1 asiento agrupado' : `${r.data.cancelados} asiento(s) individual(es)`}`);
      setCancelMasivoOpen(false); setSeleccionados([]);
      fetchDocs();
    } catch (e) { setCancelMasivoErr(e.response?.data?.error || 'Error al cancelar'); }
    setCancelandoMasivo(false);
  }

  const totalBase = docs.filter(d => d.estado !== 'ANULADO').reduce((s, d) => s + parseFloat(d.base_gravada || 0), 0);
  const totalIgv = docs.filter(d => d.estado !== 'ANULADO').reduce((s, d) => s + parseFloat(d.igv || 0), 0);
  const totalMonto = docs.filter(d => d.estado !== 'ANULADO').reduce((s, d) => s + parseFloat(d.total || 0), 0);

  const puedeConsultar = (form.tipo_doc_identidad === '6' && form.num_documento.length === 11) ||
                         (form.tipo_doc_identidad === '1' && form.num_documento.length === 8);

  return (
    <Box sx={{ width: '100%', maxWidth: 1600, mx: 'auto', mt: 1 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>Registro de Compras</Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => { setEmpresaId(e.target.value); setPeriodoSel(''); }} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Período" value={periodoSel} onChange={e => setPeriodoSel(e.target.value)} disabled={!empresaId} sx={{ minWidth: '20em' }}>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Estado" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} sx={{ minWidth: '20em' }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PENDIENTE">Pendiente</MenuItem>
              <MenuItem value="CANCELADO">Cancelado</MenuItem>
              <MenuItem value="ANULADO">Anulado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField fullWidth size="small" placeholder="Buscar RUC, proveedor, serie..." value={buscar} onChange={e => setBuscar(e.target.value)} sx={{ minWidth: '26em' }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }} />
          </Grid>
          <Grid item xs={12} sm={12} md="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Descargar plantilla Excel para importar compras">
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDescargarPlantilla}>
                Plantilla
              </Button>
            </Tooltip>
            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} disabled={!empresaId}
              onClick={() => { setImportFile(null); setImportPreview(null); setImportErr(''); setImportOpen(true); }}>
              Importar
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!empresaId || !periodoSel}
              onClick={() => { setForm({ ...EMPTY_FORM, fecha_emision: new Date().toISOString().substring(0,10) }); setIgvManual(false); setSaveErr(''); setConsultaErr(''); setOpen(true); }}>
              Nuevo
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {seleccionados.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#FFF8E1' }}>
          <Typography variant="body2">
            <b>{seleccionados.length}</b> documento(s) seleccionado(s) — Total: <b>S/ {fmt(totalSeleccionado)}</b>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => setSeleccionados([])}>Limpiar selección</Button>
            <Button size="small" variant="contained" color="success" startIcon={<DoneAllIcon />}
              onClick={() => { setCancelMasivoForm({ modo: 'individual', fecha_cancelacion: new Date().toISOString().substring(0,10), cuenta_cobpag_id: '' }); setCancelMasivoErr(''); setCancelMasivoOpen(true); }}>
              Cancelar seleccionados
            </Button>
          </Box>
        </Paper>
      )}

      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" indeterminate={seleccionados.length > 0 && seleccionados.length < pendientesDocs.length}
                    checked={pendientesDocs.length > 0 && seleccionados.length === pendientesDocs.length}
                    disabled={pendientesDocs.length === 0}
                    onChange={() => setSeleccionados(seleccionados.length === pendientesDocs.length ? [] : pendientesDocs.map(d => d.id))} />
                </TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Serie-Número</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell align="right">Base Grav.</TableCell>
                <TableCell align="right">IGV/CF</TableCell>
                <TableCell align="right">Total/Neto</TableCell>
                <TableCell>Mon.</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map(d => (
                <TableRow key={d.id} hover sx={{ opacity: d.estado === 'ANULADO' ? 0.5 : 1 }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={seleccionados.includes(d.id)} disabled={d.estado !== 'PENDIENTE'}
                      onChange={() => toggleSeleccion(d.id)} />
                  </TableCell>
                  <TableCell>{d.tipo_documento === '02' ? 'RHE' : d.tipo_documento}</TableCell>
                  <TableCell>{d.serie}-{d.numero}</TableCell>
                  <TableCell>{fmtFecha(d.fecha_emision)}</TableCell>
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.razon_social}</TableCell>
                  <TableCell align="right">{fmt(d.base_gravada)}</TableCell>
                  <TableCell align="right">{d.tipo_documento === '02' ? fmt(d.renta_4ta) + ' (ret)' : fmt(d.igv)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(d.total)}</TableCell>
                  <TableCell>{d.moneda || 'PEN'}</TableCell>
                  <TableCell>{chipEstado(d.estado)}</TableCell>
                  <TableCell>
                    {d.estado === 'PENDIENTE' && (
                      <>
                        <Tooltip title="Registrar pago"><IconButton size="small" color="success" onClick={() => { setPagoDoc(d); setPagoForm({ fecha_cancelacion: new Date().toISOString().substring(0,10), cuenta_cobpag_id: '', monto_cancelado: d.total, glosa: '', medio_pago: 'EFECTIVO', pago_referencia: '' }); setPagoDialog(true); }}><PaymentIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Anular"><IconButton size="small" color="error" onClick={() => handleAnular(d.id)}><BlockIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {docs.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay documentos en este período</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {docs.length > 0 && (
        <Paper sx={{ mt: 2, p: 2, borderRadius: 2, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Box><Typography variant="caption" color="text.secondary">Base Gravada</Typography><Typography fontWeight="bold">S/ {fmt(totalBase)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">IGV / Crédito Fiscal</Typography><Typography fontWeight="bold">S/ {fmt(totalIgv)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Total Compras</Typography><Typography fontWeight="bold" color="error.main">S/ {fmt(totalMonto)}</Typography></Box>
        </Paper>
      )}

      {snack && <Alert severity="success" onClose={() => setSnack('')} sx={{ mt: 2 }}>{snack}</Alert>}

      {/* ── Dialog Nuevo ── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Typography fontWeight="bold">Nuevo Documento de Compra</Typography></DialogTitle>
        <DialogContent>
          {saveErr && <Alert severity="error" sx={{ mb: 1 }}>{saveErr}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>

            {/* ── Fila 1: Tipo Documento | Serie | Número ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField select size="small" label="Tipo Documento" name="tipo_documento" value={form.tipo_documento} onChange={handleChange}
                sx={{ width: { xs: '100%', sm: '22em' } }}>
                {TIPOS_DOC_COMPRA.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Serie" name="serie" value={form.serie} onChange={handleChange}
                sx={{ width: '10em' }} />
              <TextField size="small" label="Número" name="numero" value={form.numero} onChange={handleChange}
                sx={{ flex: 1, minWidth: '8em' }} />
            </Box>

            {/* ── Fila 2: Fecha Emisión | Fecha Vcto | Moneda | T.C. ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Fecha Emisión" name="fecha_emision" type="date" value={form.fecha_emision} onChange={handleChange}
                InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: '10em' }} />
              <TextField size="small" label="Fecha Vcto / Pago" name="fecha_vencimiento" type="date" value={form.fecha_vencimiento} onChange={handleChange}
                InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: '10em' }} />
              <TextField select size="small" label="Moneda" name="moneda" value={form.moneda} onChange={handleChange}
                sx={{ width: '9em' }}>
                <MenuItem value="PEN">S/ PEN</MenuItem>
                <MenuItem value="USD">$ USD</MenuItem>
              </TextField>
              <TextField size="small" label="T.C." name="tipo_cambio" value={form.tipo_cambio} onChange={handleChange}
                disabled={form.moneda === 'PEN'} sx={{ width: '7em' }} />
              {usaCentroCosto && (
                <TextField select size="small" label="Centro de Costo" name="centro_costo_id" value={form.centro_costo_id}
                  onChange={handleChange} sx={{ flex: 1, minWidth: '13em' }}>
                  <MenuItem value="">— Sin asignar —</MenuItem>
                  {centrosCosto.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>
                  ))}
                </TextField>
              )}
            </Box>

            {/* ── Label: Datos del Proveedor ── */}
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ textAlign: 'center' }}>
              Datos del Proveedor
            </Typography>

            {/* ── Fila 3: Tipo Doc Identidad | Nro Documento | Razón Social ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField select size="small" label="Tipo Doc. Identidad" name="tipo_doc_identidad"
                value={form.tipo_doc_identidad} onChange={handleChange}
                sx={{ width: { xs: '100%', sm: '10em' } }}>
                <MenuItem value="6">RUC</MenuItem>
                <MenuItem value="1">DNI</MenuItem>
                <MenuItem value="4">Carnet Ext.</MenuItem>
                <MenuItem value="0">Sin doc</MenuItem>
              </TextField>
              <TextField size="small" label="Nro. Documento" name="num_documento" value={form.num_documento} onChange={handleChange}
                onKeyDown={e => { if (e.key === 'Enter' && puedeConsultar) consultarDocumento(); }}
                sx={{ width: { xs: '100%', sm: '12em' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={form.tipo_doc_identidad === '6' ? 'Consultar SUNAT' : form.tipo_doc_identidad === '1' ? 'Consultar RENIEC' : 'Consultar'}>
                        <span>
                          <IconButton size="small" onClick={consultarDocumento}
                            disabled={consultandoDoc || !puedeConsultar}
                            sx={{ color: 'primary.main' }}>
                            {consultandoDoc ? <CircularProgress size={16} /> : <ManageSearchIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
                helperText={consultaErr ? <span style={{ color: 'red' }}>{consultaErr}</span> : (form.tipo_doc_identidad === '6' ? 'RUC 11 dígitos → SUNAT' : form.tipo_doc_identidad === '1' ? 'DNI 8 dígitos → RENIEC' : '')}
              />
              <TextField size="small" label="Razón Social / Nombre" name="razon_social"
                value={form.razon_social} onChange={handleChange} sx={{ flex: 1, minWidth: '26em' }} />
            </Box>

            {/* ── Label: Importes ── */}
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ textAlign: 'center' }}>
              Importes {isRhe && <Chip label="RHE: retención 8%" size="small" color="warning" sx={{ ml: 1 }} />}
            </Typography>

            {/* ── Importes según tipo ── */}
            {!isRhe ? (
              <>
                {/* Fila 4: Base Gravada | IGV | Crédito Fiscal */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  <TextField size="small" label="Base Gravada" name="base_gravada" value={form.base_gravada}
                    onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
                  <TextField size="small" label={igvManual ? 'IGV' : 'IGV (automático 18%)'} name="igv" value={form.igv}
                    onChange={handleChange} type="number" inputProps={{ step: '0.01' }} disabled={!igvManual}
                    sx={{ flex: 1, minWidth: '9em' }} />
                  <TextField size="small" label="Crédito Fiscal" name="igv_credito_fiscal" value={form.igv_credito_fiscal}
                    onChange={handleChange} type="number" inputProps={{ step: '0.01' }} disabled sx={{ flex: 1, minWidth: '9em' }} />
                  <FormControlLabel sx={{ ml: 0 }} control={
                    <Checkbox size="small" checked={igvManual} onChange={e => handleToggleIgvManual(e.target.checked)} />
                  } label={<Typography variant="caption">Editar IGV manualmente</Typography>} />
                </Box>

                {/* Fila 4b: Destino de la operación — para el Registro de Compras formato SUNAT (prorrateo) */}
                <TextField select size="small" label="Destino de la operación (Registro de Compras SUNAT)"
                  name="destino_operacion" value={form.destino_operacion}
                  onChange={handleChange} sx={{ minWidth: '26em' }}>
                  {DESTINO_OPERACION_COMPRA.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>

                {/* Fila 5: Base Exonerada | Base Inafecta | ISC */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <TextField size="small" label="Base Exonerada" name="base_exonerada" value={form.base_exonerada}
                    onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
                  <TextField size="small" label="Base Inafecta" name="base_inafecta" value={form.base_inafecta}
                    onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
                  <TextField size="small" label="ISC" name="isc" value={form.isc}
                    onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
                </Box>
              </>
            ) : (
              /* RHE: Honorarios | Retención */
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <TextField size="small" label="Honorarios (bruto)" name="base_gravada" value={form.base_gravada}
                  onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '12em' }} />
                <TextField size="small" label="Retención 4ta cat. (8%)" name="renta_4ta" value={form.renta_4ta}
                  onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '12em' }} />
              </Box>
            )}

            {/* ── Fila 6: Total | Glosa ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label={isRhe ? 'Neto a pagar' : 'Total'} name="total" value={form.total}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }}
                InputProps={{ sx: { fontWeight: 'bold', bgcolor: '#fff8f0' } }}
                sx={{ width: '11em' }} />
              <TextField size="small" label="Glosa (opcional)" name="glosa" value={form.glosa}
                onChange={handleChange} sx={{ flex: 1, minWidth: '14em' }} />
            </Box>

            {/* ── Datos SUNAT adicionales (opcional): NC/ND, detracción, retención, percepción, aduana ── */}
            <Box>
              <Button size="small" onClick={() => setShowAdicionales(v => !v)}
                endIcon={<ExpandMoreIcon sx={{ transform: showAdicionales ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Datos SUNAT adicionales (opcional) — doc. referencia, detracción, retención, percepción, aduana
              </Button>
              <Collapse in={showAdicionales}>
                <Box sx={{ p: 1.5, mt: 0.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  {(form.tipo_documento === '07' || form.tipo_documento === '08') && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                      <Divider sx={{ width: '100%' }}><Typography variant="caption" color="text.secondary" fontWeight="bold">DOCUMENTO DE REFERENCIA</Typography></Divider>
                      <TextField select size="small" label="Tipo Doc. Ref." name="doc_ref_tipo" value={form.doc_ref_tipo}
                        onChange={handleChange} sx={{ minWidth: '14em' }}>
                        <MenuItem value=""><em>—</em></MenuItem>
                        {TIPOS_DOC_COMPRA.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
                      </TextField>
                      <TextField size="small" label="Serie Ref." name="doc_ref_serie"
                        value={form.doc_ref_serie} onChange={e => setForm(f => ({ ...f, doc_ref_serie: e.target.value.toUpperCase() }))}
                        onBlur={() => autocompletarDesdeReferencia(form.doc_ref_serie, form.doc_ref_numero)}
                        sx={{ width: '9em' }} inputProps={{ style: { fontFamily: 'monospace' } }} />
                      <TextField size="small" label="Número Ref." name="doc_ref_numero" value={form.doc_ref_numero}
                        onChange={handleChange}
                        onBlur={() => autocompletarDesdeReferencia(form.doc_ref_serie, form.doc_ref_numero)}
                        sx={{ width: '10em' }} inputProps={{ style: { fontFamily: 'monospace' } }}
                        helperText="Al completar, hereda moneda y T.C. del documento original" />
                    </Box>
                  )}

                  {(form.tipo_documento === '50' || form.tipo_documento === '52') && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                      <Divider sx={{ width: '100%' }}><Typography variant="caption" color="text.secondary" fontWeight="bold">DATOS ADUANA</Typography></Divider>
                      <TextField size="small" label="Código Aduana" name="aduana_codigo" value={form.aduana_codigo}
                        onChange={handleChange} sx={{ width: '10em' }} inputProps={{ maxLength: 5 }} placeholder="0008" />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel control={
                      <Checkbox size="small" checked={form.tiene_detraccion}
                        onChange={e => setForm(f => ({ ...f, tiene_detraccion: e.target.checked }))} />
                    } label="Detracción SPOT" />
                    <FormControlLabel control={
                      <Checkbox size="small" checked={form.tiene_retencion}
                        onChange={e => setForm(f => ({ ...f, tiene_retencion: e.target.checked }))} />
                    } label="Retención" />
                    <FormControlLabel control={
                      <Checkbox size="small" checked={form.tiene_percepcion}
                        onChange={e => setForm(f => ({ ...f, tiene_percepcion: e.target.checked }))} />
                    } label="Percepción" />
                  </Box>

                  {form.tiene_detraccion && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                      <TextField label="Fecha SPOT" type="date" size="small" value={form.det_fecha}
                        InputLabelProps={{ shrink: true }} onChange={e => setForm(f => ({ ...f, det_fecha: e.target.value }))}
                        sx={{ width: '10em' }} />
                      <TextField label="N° Constancia" size="small" value={form.det_numero}
                        onChange={e => setForm(f => ({ ...f, det_numero: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ style: { fontFamily: 'monospace' } }} />
                      <TextField label="Tasa %" type="number" size="small" value={form.det_tasa}
                        onChange={e => setForm(f => ({ ...f, det_tasa: e.target.value }))}
                        sx={{ width: '7em' }} inputProps={{ min: 0, max: 100, step: 0.01 }} />
                      <TextField label="Monto S/" type="number" size="small" value={form.det_monto}
                        onChange={e => setForm(f => ({ ...f, det_monto: e.target.value }))}
                        sx={{ width: '8em' }} inputProps={{ min: 0, step: 0.01 }} />
                      <TextField label="Tipo SPOT (tabla 54)" size="small" value={form.det_tipo}
                        onChange={e => setForm(f => ({ ...f, det_tipo: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }} placeholder="022" />
                    </Box>
                  )}

                  {form.tiene_retencion && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                      <TextField label="Código Tasa Ret." size="small" value={form.ret_codigo_tasa}
                        onChange={e => setForm(f => ({ ...f, ret_codigo_tasa: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }}
                        placeholder="01" helperText="01=3%, 02=6%" />
                      <TextField label="N° Retención" size="small" value={form.ret_numero}
                        onChange={e => setForm(f => ({ ...f, ret_numero: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                    </Box>
                  )}

                  {form.tiene_percepcion && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                      <TextField label="Código Tasa Per." size="small" value={form.per_codigo_tasa}
                        onChange={e => setForm(f => ({ ...f, per_codigo_tasa: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }}
                        placeholder="01" helperText="01=2%, 02=1%" />
                      <TextField label="N° Percepción" size="small" value={form.per_numero}
                        onChange={e => setForm(f => ({ ...f, per_numero: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} startIcon={<CloseIcon />} color="error" variant="outlined">Cancelar</Button>
          <Button onClick={handleSave} startIcon={<SaveIcon />} variant="contained">Registrar y generar asiento</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Pago ── */}
      <Dialog open={pagoDialog} onClose={() => setPagoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Registrar Pago — {pagoDoc?.serie}-{pagoDoc?.numero}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Fecha de Pago" type="date" value={pagoForm.fecha_cancelacion}
                onChange={e => setPagoForm({ ...pagoForm, fecha_cancelacion: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: '12em' } }} />
              <TextField size="small" label="Monto pagado" type="number" value={pagoForm.monto_cancelado}
                onChange={e => setPagoForm({ ...pagoForm, monto_cancelado: e.target.value })}
                sx={{ width: { xs: '100%', sm: '11em' } }} />
              <TextField select size="small" label="Medio de Pago" value={pagoForm.medio_pago}
                onChange={e => setPagoForm({ ...pagoForm, medio_pago: e.target.value })}
                sx={{ width: { xs: '100%', sm: '12em' } }}>
                {MEDIOS_PAGO.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField size="small" label="N° Documento de Pago" placeholder="Voucher / operación"
                value={pagoForm.pago_referencia} onChange={e => setPagoForm({ ...pagoForm, pago_referencia: e.target.value })}
                sx={{ flex: 1, minWidth: '14em' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: '22em' }}
                options={cuentasCaja}
                getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                onChange={(_, v) => setPagoForm({ ...pagoForm, cuenta_cobpag_id: v?.id || '' })}
                renderInput={params => <TextField {...params} size="small" label="Cuenta Caja/Banco" placeholder="Buscar cuenta 10x..." />}
                filterOptions={(opts, { inputValue }) => opts.filter(o =>
                  o.codigo?.includes(inputValue) || o.descripcion?.toLowerCase().includes(inputValue.toLowerCase())
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Glosa (opcional)" value={pagoForm.glosa}
                onChange={e => setPagoForm({ ...pagoForm, glosa: e.target.value })} sx={{ flex: 1, minWidth: '14em' }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagoDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handlePago} startIcon={<PaymentIcon />}>Registrar pago</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Importar Excel ── */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importar Compras desde Excel</DialogTitle>
        <DialogContent>
          {importErr && <Alert severity="error" sx={{ mb: 1.5 }}>{importErr}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Descargue la <b>Plantilla</b> (botón de arriba), complétela con sus compras y súbala aquí.
            Cada fila debe tener "Tipo Operación" = COMPRA.
          </Typography>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={importing}>
            Seleccionar archivo
            <input type="file" hidden accept=".xlsx" onChange={e => {
              const f = e.target.files?.[0];
              if (f) { setImportFile(f); handleImportarPreview(f); }
            }} />
          </Button>
          {importFile && <Chip sx={{ ml: 1.5 }} label={importFile.name} onDelete={() => { setImportFile(null); setImportPreview(null); }} />}
          {importing && <CircularProgress size={20} sx={{ ml: 1.5 }} />}

          {importPreview && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={importPreview.errores?.length ? 'warning' : 'success'} sx={{ mb: 1 }}>
                Se encontraron <b>{importPreview.total}</b> documento(s) válido(s) para importar.
                {importPreview.errores?.length > 0 && ` ${importPreview.errores.length} fila(s) con observaciones (no se importarán).`}
              </Alert>
              {importPreview.errores?.length > 0 && (
                <Box sx={{ maxHeight: 120, overflowY: 'auto', mb: 1, fontSize: '0.8rem', color: '#b26a00' }}>
                  {importPreview.errores.map((e, i) => <div key={i}>• {e}</div>)}
                </Box>
              )}
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Serie-Número</TableCell><TableCell>Fecha</TableCell>
                      <TableCell>Proveedor</TableCell><TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.docs.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell>{d.serie}-{d.numero}</TableCell>
                        <TableCell>{d.fecha_emision}</TableCell>
                        <TableCell>{d.razon_social}</TableCell>
                        <TableCell align="right">{fmt(d.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>Cerrar</Button>
          <Button variant="contained" disabled={!importPreview || importPreview.total === 0 || importing}
            onClick={handleImportarConfirmar} startIcon={importing ? <CircularProgress size={16} /> : <SaveIcon />}>
            Confirmar importación
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Cancelación Masiva ── */}
      <Dialog open={cancelMasivoOpen} onClose={() => !cancelandoMasivo && setCancelMasivoOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Cancelar {seleccionados.length} documento(s)</DialogTitle>
        <DialogContent>
          {cancelMasivoErr && <Alert severity="error" sx={{ mb: 1.5 }}>{cancelMasivoErr}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>Total a pagar: <b>S/ {fmt(totalSeleccionado)}</b></Typography>

          <FormLabel sx={{ fontSize: '0.85rem' }}>¿Cómo desea cancelarlos?</FormLabel>
          <RadioGroup value={cancelMasivoForm.modo} onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, modo: e.target.value })}>
            <FormControlLabel value="individual" control={<Radio size="small" />}
              label={`Un asiento por cada documento (${seleccionados.length} asientos)`} />
            <FormControlLabel value="agrupado" control={<Radio size="small" />} disabled={cancelMasivoForm.usar_fecha_emision}
              label="Un solo asiento agrupado para todos" />
          </RadioGroup>

          <FormControlLabel sx={{ mt: 0.5 }} control={
            <Checkbox size="small" checked={cancelMasivoForm.usar_fecha_emision}
              onChange={e => setCancelMasivoForm({
                ...cancelMasivoForm,
                usar_fecha_emision: e.target.checked,
                modo: e.target.checked ? 'individual' : cancelMasivoForm.modo,
                fecha_cancelacion: e.target.checked ? '' : cancelMasivoForm.fecha_cancelacion,
              })}
            />
          } label={<Typography variant="body2">Usar la fecha de emisión de cada comprobante como su fecha de cancelación</Typography>} />
          {cancelMasivoForm.usar_fecha_emision && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Cada documento se cancelará con su propia fecha de emisión — por eso solo aplica en modo individual.
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Fecha de pago" type="date" InputLabelProps={{ shrink: true }}
                value={cancelMasivoForm.fecha_cancelacion} disabled={cancelMasivoForm.usar_fecha_emision}
                onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, fecha_cancelacion: e.target.value })}
                sx={{ width: { xs: '100%', sm: '12em' } }} />
              <TextField select size="small" label="Medio de Pago" value={cancelMasivoForm.medio_pago}
                onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, medio_pago: e.target.value })}
                sx={{ width: { xs: '100%', sm: '12em' } }}>
                {MEDIOS_PAGO.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField size="small" label="N° Documento de Pago" placeholder="Voucher / operación"
                value={cancelMasivoForm.pago_referencia}
                onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, pago_referencia: e.target.value })}
                sx={{ flex: 1, minWidth: '14em' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: '22em' }}
                options={cuentasCaja}
                getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                onChange={(_, v) => setCancelMasivoForm({ ...cancelMasivoForm, cuenta_cobpag_id: v?.id || '' })}
                renderInput={params => <TextField {...params} size="small" label="Cuenta Caja/Banco" />}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelMasivoOpen(false)} disabled={cancelandoMasivo}>Cerrar</Button>
          <Button variant="contained" color="success" onClick={handleCancelarMasivo}
            startIcon={cancelandoMasivo ? <CircularProgress size={16} /> : <DoneAllIcon />} disabled={cancelandoMasivo}>
            Confirmar cancelación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
